import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Home, 
  Gift, 
  Users, 
  Landmark, 
  Calculator, 
  Info,
  AlertTriangle,
  Euro,
  Building,
  User,
  Clock,
  BadgePercent,
  FileText,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Receipt
} from 'lucide-react';

// =============================================
// CONSTANTE ȘI FORMULE CONFORM LEGISLAȚIEI 2026
// =============================================

// Taxa ANCPI - Intabulare drept de proprietate
const TARIF_ANCPI_PROPRIETATE = {
  PF: 0.0015,  // 0.15% × valoarea din act
  PJ: 0.005    // 0.5% × valoarea din act
};

// Taxa ANCPI - Intabulare ipotecă
const TARIF_ANCPI_IPOTECA = {
  PROCENT: 0.001,  // 0.1% × valoarea creditului
  FIX_PER_CF: 100  // + 100 lei / imobil / CF
};

// Extras de Carte Funciară pentru autentificare
const EXTRAS_CF = 40; // lei, FĂRĂ TVA

// Verificare procură bancă (INFONOT)
const VERIFICARE_PROCURA = 15; // lei + TVA

// Verificare regim matrimonial (RNNRM)
const VERIFICARE_REGIM_MATRIMONIAL = 18; // lei, FĂRĂ TVA

// Impozit transfer proprietate (încasat de notar, virat la stat)
const IMPOZIT_TRANSFER = {
  VANZARE_SUB_3_ANI: 0.03,   // 3% - deținut ≤ 3 ani
  VANZARE_PESTE_3_ANI: 0.01, // 1% - deținut > 3 ani
  MOSTENIRE_SUB_2_ANI: 0,    // 0%
  MOSTENIRE_PESTE_2_ANI: 0.01 // 1%
};

// Tranșe onorariu notarial VÂNZARE - Anexa nr. 2 pct. I (bunuri imobile)
const TRANSE_ONORARIU = [
  { limita: 20000, procent: 0.022, baza: 0, minim: 230 },        // 2,2% (minim 230 lei)
  { limita: 35000, procent: 0.019, baza: 440 },                   // 440 + 1,9%
  { limita: 65000, procent: 0.016, baza: 725 },                   // 725 + 1,6%
  { limita: 100000, procent: 0.015, baza: 1205 },                 // 1.205 + 1,5%
  { limita: 200000, procent: 0.011, baza: 1705 },                 // 1.705 + 1,1%
  { limita: 600000, procent: 0.009, baza: 2805 },                 // 2.805 + 0,9%
  { limita: Infinity, procent: 0.006, baza: 6405 }                // 6.405 + 0,6%
];

// Tranșe onorariu IPOTECĂ - Anexa nr. 2 pct. V (acte de garanție)
const TRANSE_IPOTECA = [
  { limita: 50000, procent: 0.0085, baza: 0, minim: 150 },       // 0,85% (minim 150 lei)
  { limita: 100000, procent: 0.005, baza: 425 },                  // 425 + 0,5%
  { limita: 200000, procent: 0.0046, baza: 750 },                 // 750 + 0,46%
  { limita: 500000, procent: 0.0019, baza: 1209 },                // 1.209 + 0,19%
  { limita: Infinity, procent: 0.001, baza: 1778 }                // 1.778 + 0,10%
];

// Reduceri aplicabile la onorariu
const REDUCERI = {
  PRIMA_CASA: 0.30,      // -30%
  DARE_IN_PLATA: 0.50,   // -50%
  MAGISTRAT: 0.70        // -70%
};

// Taxa radiere ipotecă (onorariu minim)
const TAXA_RADIERE_IPOTECA = 150; // lei + TVA

// Curs BNR
const CURS_EUR = 5.0905;

// =============================================
// TAXE ANTECONTRACT / PROMISIUNE BILATERALĂ
// =============================================
const ANTECONTRACT = {
  ONORARIU_PROCENT: 0.01,      // 1% din valoarea plătită
  ONORARIU_MINIM: 230,          // minim 230 lei
  EXTRAS_CF_INFORMARE: 20,      // 20 lei / carte funciară
  EXTRAS_CF_AUTENTIFICARE: 40,  // 40 lei / carte funciară
  NOTARE_CF: 75                 // notarea la CF a promisiunii bilaterale
};

// =============================================
// FUNCȚII DE CALCUL
// =============================================

// Calcul onorariu notarial VÂNZARE (Anexa nr. 2 pct. I)
const calculeazaOnorariu = (valoare) => {
  if (valoare <= 0) return 0;
  
  let onorariu = 0;
  let limitaAnterioara = 0;
  
  for (const transa of TRANSE_ONORARIU) {
    if (valoare <= transa.limita) {
      if (transa.baza === 0) {
        onorariu = Math.max(valoare * transa.procent, transa.minim || 0);
      } else {
        onorariu = transa.baza + (valoare - limitaAnterioara) * transa.procent;
      }
      break;
    }
    limitaAnterioara = transa.limita;
  }
  
  return Math.round(onorariu * 100) / 100;
};

// Calcul onorariu IPOTECĂ (Anexa nr. 2 pct. V - acte de garanție)
const calculeazaOnoraruIpoteca = (valoareCredit) => {
  if (valoareCredit <= 0) return 0;
  
  let onorariu = 0;
  
  if (valoareCredit <= 50000) {
    onorariu = Math.max(valoareCredit * 0.0085, 150);
  } else if (valoareCredit <= 100000) {
    onorariu = 425 + (valoareCredit - 50000) * 0.005;
  } else if (valoareCredit <= 200000) {
    onorariu = 750 + (valoareCredit - 100000) * 0.0046;
  } else if (valoareCredit <= 500000) {
    onorariu = 1209 + (valoareCredit - 200000) * 0.0019;
  } else {
    onorariu = 1778 + (valoareCredit - 500000) * 0.001;
  }
  
  return Math.round(onorariu * 100) / 100;
};

// Calcul taxa ANCPI intabulare drept de proprietate (FĂRĂ TVA)
const calculeazaTaxaANCPI_Proprietate = (valoare, tipProprietar) => {
  const procent = tipProprietar === 'PJ' ? TARIF_ANCPI_PROPRIETATE.PJ : TARIF_ANCPI_PROPRIETATE.PF;
  return Math.round(valoare * procent);
};

// Calcul taxa ANCPI intabulare ipotecă (FĂRĂ TVA)
const calculeazaTaxaANCPI_Ipoteca = (valoareCredit) => {
  // 0.1% × valoarea creditului + 100 lei / imobil / CF
  return Math.round(valoareCredit * TARIF_ANCPI_IPOTECA.PROCENT) + TARIF_ANCPI_IPOTECA.FIX_PER_CF;
};

// Calcul impozit transfer (FĂRĂ TVA - taxă către stat)
const calculeazaImpozit = (valoare, tipTranzactie, durata, tipSuccesiune = null) => {
  if (tipTranzactie === 'succesiune') {
    if (tipSuccesiune === 'sub_2_ani') return 0;
    return Math.round(valoare * IMPOZIT_TRANSFER.MOSTENIRE_PESTE_2_ANI);
  }
  
  if (tipTranzactie === 'vanzare' || tipTranzactie === 'donatie') {
    if (durata === 'sub_3_ani') {
      return Math.round(valoare * IMPOZIT_TRANSFER.VANZARE_SUB_3_ANI);
    }
    return Math.round(valoare * IMPOZIT_TRANSFER.VANZARE_PESTE_3_ANI);
  }
  
  return 0;
};

const aplicaReducere = (onorariu, tipReducere) => {
  if (!tipReducere || tipReducere === 'niciunul') return onorariu;
  
  const reducere = REDUCERI[tipReducere.toUpperCase().replace(' ', '_')] || 0;
  return onorariu * (1 - reducere);
};

// Calcul onorariu ANTECONTRACT / PROMISIUNE BILATERALĂ
const calculeazaOnoraruAntecontract = (valoarePlatita) => {
  if (valoarePlatita <= 0) return ANTECONTRACT.ONORARIU_MINIM;
  const onorariu = valoarePlatita * ANTECONTRACT.ONORARIU_PROCENT;
  return Math.max(Math.round(onorariu), ANTECONTRACT.ONORARIU_MINIM);
};

// =============================================
// COMPONENTA PRINCIPAL
// =============================================

const NotaryFeesCalculator = () => {
  const [tipTranzactie, setTipTranzactie] = useState('antecontract');
  const [valoareDeclarata, setValoareDeclarata] = useState('');
  const [valoareStudiu, setValoareStudiu] = useState('');
  const [valoareCredit, setValoareCredit] = useState(''); // Valoarea creditului pentru ipotecă
  // State pentru Antecontract
  const [valoarePlatitaAntecontract, setValoarePlatitaAntecontract] = useState('');
  const [numarCF, setNumarCF] = useState(1); // Număr de cărți funciare
  const [includeExtrasCFInformare, setIncludeExtrasCFInformare] = useState(true); // De obicei se scoate
  const [includeExtrasCFAutentificare, setIncludeExtrasCFAutentificare] = useState(true);
  const [includeNotareCF, setIncludeNotareCF] = useState(true);
  
  const [tipProprietar, setTipProprietar] = useState('PF');
  const [durataDetinere, setDurataDetinere] = useState('peste_3_ani');
  const [programSpecial, setProgramSpecial] = useState('niciunul');
  const [situatieSpeciala, setSituatieSpeciala] = useState('niciunul');
  const [modPlata, setModPlata] = useState('fara_credit');
  const [esteRefinantare, setEsteRefinantare] = useState(false); // Pentru radiere ipotecă veche
  const [moneda, setMoneda] = useState('RON');
  const [showDetails, setShowDetails] = useState(false);
  const [tvaPercent, setTvaPercent] = useState(21); // TVA în vigoare 21%
  
  // Rezultate calculate
  const [rezultate, setRezultate] = useState(null);

  const getValoareInRON = (valoare) => {
    if (!valoare) return 0;
    const val = parseFloat(valoare) || 0;
    return moneda === 'EUR' ? val * CURS_EUR : val;
  };

  const calculeazaTotal = () => {
    const valDecRON = getValoareInRON(valoareDeclarata);
    const valStudRON = getValoareInRON(valoareStudiu);
    const valCreditRON = getValoareInRON(valoareCredit);
    
    // Regula: valoare_calcul = max(valoare_declarată, valoare_piață)
    const valoareCalcul = Math.max(valDecRON, valStudRON || 0);
    
    if (valoareCalcul <= 0) {
      setRezultate(null);
      return;
    }

    // =============================================
    // I. CONTRACT DE VÂNZARE-CUMPĂRARE (cumpărător)
    // =============================================
    
    // 1. Onorariu notarial VÂNZARE (Anexa nr. 2 pct. I)
    let onorariuVanzare = calculeazaOnorariu(valoareCalcul);
    
    // Aplică reducere dacă există (Prima Casă, Dare în plată etc.)
    const reducereAplicata = programSpecial !== 'niciunul' ? programSpecial : 
                            (situatieSpeciala === 'magistrat' ? 'MAGISTRAT' : null);
    const onorariuVanzareDupaReducere = aplicaReducere(onorariuVanzare, reducereAplicata);
    
    // TVA 21% pe onorariu vânzare
    const tvaOnorariuVanzare = Math.round(onorariuVanzareDupaReducere * (tvaPercent / 100));
    const onorariuVanzareCuTVA = Math.round(onorariuVanzareDupaReducere + tvaOnorariuVanzare);
    
    // 2. Taxa ANCPI - Intabulare drept de proprietate (FĂRĂ TVA)
    const taxaANCPI_Proprietate = calculeazaTaxaANCPI_Proprietate(valoareCalcul, tipProprietar);
    
    // 3. Extras de Carte Funciară (FĂRĂ TVA)
    const extrasCF = EXTRAS_CF; // 40 lei
    
    // =============================================
    // II. CONTRACT DE IPOTECĂ (credit bancar)
    // =============================================
    let taxeIpoteca = {
      areIpoteca: false,
      valoareCredit: 0,
      // Onorariu notarial ipotecă
      onorariuIpoteca: 0,
      tvaOnorariuIpoteca: 0,
      onorariuIpotecaCuTVA: 0,
      // Taxa ANCPI intabulare ipotecă
      taxaANCPI_Ipoteca: 0,
      // Verificare procură bancă (INFONOT)
      verificareProcura: 0,
      tvaVerificareProcura: 0,
      verificareProcuraCuTVA: 0,
      // Verificare regim matrimonial
      verificareRegimMatrimonial: 0,
      // Radiere ipotecă veche (pentru refinanțare)
      areRadiere: false,
      onorariuRadiere: 0,
      tvaRadiere: 0,
      onorariuRadiereCuTVA: 0,
      taxaRadiereCF: 0,
      // Total
      totalIpoteca: 0
    };
    
    // Dacă este credit ipotecar sau Noua Casă
    if ((modPlata === 'credit_ipotecar' || modPlata === 'credit_noua_casa') && valCreditRON > 0) {
      taxeIpoteca.areIpoteca = true;
      taxeIpoteca.valoareCredit = valCreditRON;
      
      // 1. Onorariu notarial IPOTECĂ (Anexa nr. 2 pct. V)
      taxeIpoteca.onorariuIpoteca = calculeazaOnoraruIpoteca(valCreditRON);
      taxeIpoteca.tvaOnorariuIpoteca = Math.round(taxeIpoteca.onorariuIpoteca * (tvaPercent / 100));
      taxeIpoteca.onorariuIpotecaCuTVA = Math.round(taxeIpoteca.onorariuIpoteca + taxeIpoteca.tvaOnorariuIpoteca);
      
      // 2. Taxa ANCPI - Intabulare ipotecă (FĂRĂ TVA)
      // 0.1% × valoarea creditului + 100 lei / imobil / CF
      taxeIpoteca.taxaANCPI_Ipoteca = calculeazaTaxaANCPI_Ipoteca(valCreditRON);
      
      // 3. Verificare procură bancă - INFONOT (cu TVA)
      taxeIpoteca.verificareProcura = VERIFICARE_PROCURA;
      taxeIpoteca.tvaVerificareProcura = Math.round(VERIFICARE_PROCURA * (tvaPercent / 100) * 100) / 100;
      taxeIpoteca.verificareProcuraCuTVA = Math.round((VERIFICARE_PROCURA + taxeIpoteca.tvaVerificareProcura) * 100) / 100;
      
      // 4. Verificare regim matrimonial (FĂRĂ TVA)
      taxeIpoteca.verificareRegimMatrimonial = VERIFICARE_REGIM_MATRIMONIAL;
      
      // Pentru refinanțare - radiere ipotecă veche
      if (esteRefinantare) {
        taxeIpoteca.areRadiere = true;
        taxeIpoteca.onorariuRadiere = TAXA_RADIERE_IPOTECA;
        taxeIpoteca.tvaRadiere = Math.round(TAXA_RADIERE_IPOTECA * (tvaPercent / 100));
        taxeIpoteca.onorariuRadiereCuTVA = taxeIpoteca.onorariuRadiere + taxeIpoteca.tvaRadiere;
        taxeIpoteca.taxaRadiereCF = 60; // Taxă ANCPI radiere
      }
      
      // Total taxe ipotecă
      taxeIpoteca.totalIpoteca = taxeIpoteca.onorariuIpotecaCuTVA + 
                                 taxeIpoteca.taxaANCPI_Ipoteca +
                                 taxeIpoteca.verificareProcuraCuTVA +
                                 taxeIpoteca.verificareRegimMatrimonial +
                                 (taxeIpoteca.areRadiere ? taxeIpoteca.onorariuRadiereCuTVA + taxeIpoteca.taxaRadiereCF : 0);
    }
    
    // =============================================
    // III. TAXE VÂNZĂTOR
    // =============================================
    const tipSuccesiune = tipTranzactie === 'succesiune' ? durataDetinere : null;
    const impozitTransfer = calculeazaImpozit(valoareCalcul, tipTranzactie, durataDetinere, tipSuccesiune);
    
    // =============================================
    // TOTAL CUMPĂRĂTOR vs VÂNZĂTOR
    // =============================================
    const taxeCumparator = {
      // Contract vânzare
      onorariuVanzare: onorariuVanzareDupaReducere,
      tvaOnorariuVanzare: tvaOnorariuVanzare,
      onorariuVanzareCuTVA: onorariuVanzareCuTVA,
      taxaANCPI_Proprietate: taxaANCPI_Proprietate,
      extrasCF: extrasCF,
      // Contract ipotecă (dacă există)
      taxeIpoteca: taxeIpoteca.totalIpoteca,
      // Total
      total: onorariuVanzareCuTVA + taxaANCPI_Proprietate + extrasCF + taxeIpoteca.totalIpoteca
    };
    
    const taxeVanzator = {
      impozitTransfer: impozitTransfer,
      procentImpozit: durataDetinere === 'sub_3_ani' ? 3 : 1,
      // Total
      total: impozitTransfer
    };
    
    // Total general
    const totalGeneral = taxeCumparator.total + taxeVanzator.total;

    setRezultate({
      valoareCalcul,
      // Vânzare
      onorariuVanzare,
      onorariuVanzareDupaReducere,
      tvaOnorariuVanzare,
      onorariuVanzareCuTVA,
      reducereAplicata,
      reducereProcent: reducereAplicata ? (REDUCERI[reducereAplicata.toUpperCase()] || 0) * 100 : 0,
      taxaANCPI_Proprietate,
      extrasCF,
      // Ipotecă
      taxeIpoteca,
      // Vânzător
      impozitTransfer,
      procentImpozit: durataDetinere === 'sub_3_ani' ? 3 : 1,
      // Totale
      taxeCumparator,
      taxeVanzator,
      totalGeneral,
      tvaPercent
    });
  };

  // Calculează la fiecare modificare
  useEffect(() => {
    calculeazaTotal();
  }, [valoareDeclarata, valoareStudiu, valoareCredit, tipProprietar, durataDetinere, programSpecial, 
      situatieSpeciala, tipTranzactie, modPlata, esteRefinantare, moneda, tvaPercent]);

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('ro-RO', { 
      minimumFractionDigits: 0,
      maximumFractionDigits: 0 
    }).format(Math.round(value)) + ' lei';
  };

  const getTipTranzactieLabel = () => {
    switch (tipTranzactie) {
      case 'vanzare': return 'vânzare';
      case 'donatie': return 'donație';
      case 'succesiune': return 'bunuri moștenite';
      default: return 'tranzacție';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calculator className="w-6 h-6 text-blue-600" />
            Calculatoare taxe notariale*
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Curs BNR {new Date().toLocaleDateString('ro-RO', { day: 'numeric', month: 'long' })}: 1€ = {CURS_EUR} lei | TVA: {tvaPercent}%
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* TVA Input */}
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-gray-600 whitespace-nowrap">TVA:</Label>
            <div className="flex items-center">
              <Input
                type="number"
                value={tvaPercent}
                onChange={(e) => setTvaPercent(Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))}
                className="w-16 h-8 text-center text-sm"
                min="0"
                max="100"
                step="1"
              />
              <span className="ml-1 text-sm text-gray-600">%</span>
            </div>
          </div>
          {/* Currency buttons */}
          <div className="flex items-center gap-2">
            <Button
              variant={moneda === 'EUR' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMoneda('EUR')}
              className={moneda === 'EUR' ? 'bg-blue-600' : ''}
            >
              €
            </Button>
            <Button
              variant={moneda === 'RON' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMoneda('RON')}
              className={moneda === 'RON' ? 'bg-blue-600' : ''}
            >
              Lei
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs pentru tipul tranzacției */}
      <Tabs value={tipTranzactie} onValueChange={setTipTranzactie} className="w-full">
        <TabsList className="grid w-full grid-cols-2 bg-gray-100 p-1 rounded-lg">
          <TabsTrigger 
            value="antecontract"
            className="flex items-center gap-2 data-[state=active]:bg-purple-500 data-[state=active]:text-white rounded-md"
          >
            <FileText className="w-4 h-4" />
            Antecontract
          </TabsTrigger>
          <TabsTrigger 
            value="vanzare" 
            className="flex items-center gap-2 data-[state=active]:bg-red-400 data-[state=active]:text-white rounded-md"
          >
            <Home className="w-4 h-4" />
            Vânzare-Cumpărare
          </TabsTrigger>
        </TabsList>

        {/* =============================================
            FORMULAR ANTECONTRACT / PROMISIUNE BILATERALĂ
            ============================================= */}
        {tipTranzactie === 'antecontract' && (
          <Card className="mt-4 border-2 border-purple-200">
            <CardContent className="p-6 space-y-6">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-purple-800 flex items-center gap-2 mb-2">
                  <FileText className="w-5 h-5" />
                  Antecontract / Promisiune Bilaterală de Vânzare-Cumpărare
                </h3>
                <p className="text-sm text-purple-700">
                  Onorariul se stabilește la 1% din valoarea plătită, dar nu mai puțin de 230 lei
                </p>
              </div>

              {/* Valoarea plătită */}
              <div className="space-y-2">
                <Label className="text-base font-medium">
                  Valoarea plătită prin antecontract ({moneda})
                </Label>
                <Input
                  type="number"
                  value={valoarePlatitaAntecontract}
                  onChange={(e) => setValoarePlatitaAntecontract(e.target.value)}
                  placeholder="Ex: 50000"
                  className="text-lg"
                />
                <p className="text-xs text-gray-500">
                  Introdu suma plătită la semnarea antecontractului (avans, arvună etc.)
                </p>
              </div>

              {/* Număr de cărți funciare */}
              <div className="space-y-2">
                <Label className="text-base font-medium">
                  Număr de cărți funciare (CF)
                </Label>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setNumarCF(Math.max(1, numarCF - 1))}
                    className="w-10 h-10"
                  >
                    -
                  </Button>
                  <span className="text-xl font-bold w-10 text-center">{numarCF}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setNumarCF(numarCF + 1)}
                    className="w-10 h-10"
                  >
                    +
                  </Button>
                </div>
              </div>

              {/* Opțiuni extras CF */}
              <div className="space-y-3">
                <Label className="text-base font-medium">Extrase Carte Funciară</Label>
                
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <input 
                    type="checkbox" 
                    id="extrasCFInformare"
                    checked={includeExtrasCFInformare}
                    onChange={(e) => setIncludeExtrasCFInformare(e.target.checked)}
                    className="w-5 h-5 text-blue-600 mt-0.5"
                  />
                  <div>
                    <Label htmlFor="extrasCFInformare" className="font-medium cursor-pointer">
                      Extras CF de informare ({ANTECONTRACT.EXTRAS_CF_INFORMARE} lei / CF)
                    </Label>
                    <p className="text-xs text-gray-600 mt-1">
                      De obicei se scoate pentru verificarea situației juridice a imobilului
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <input 
                    type="checkbox" 
                    id="extrasCFAutentificare"
                    checked={includeExtrasCFAutentificare}
                    onChange={(e) => setIncludeExtrasCFAutentificare(e.target.checked)}
                    className="w-5 h-5 text-green-600 mt-0.5"
                  />
                  <div>
                    <Label htmlFor="extrasCFAutentificare" className="font-medium cursor-pointer">
                      Extras CF de autentificare ({ANTECONTRACT.EXTRAS_CF_AUTENTIFICARE} lei / CF)
                    </Label>
                    <p className="text-xs text-gray-600 mt-1">
                      Obligatoriu la semnarea antecontractului la notar
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <input 
                    type="checkbox" 
                    id="notareCF"
                    checked={includeNotareCF}
                    onChange={(e) => setIncludeNotareCF(e.target.checked)}
                    className="w-5 h-5 text-amber-600 mt-0.5"
                  />
                  <div>
                    <Label htmlFor="notareCF" className="font-medium cursor-pointer">
                      Notare promisiune bilaterală în CF ({ANTECONTRACT.NOTARE_CF} lei)
                    </Label>
                    <p className="text-xs text-gray-600 mt-1">
                      Recomandat pentru protejarea drepturilor cumpărătorului
                    </p>
                  </div>
                </div>
              </div>

              {/* REZULTATE ANTECONTRACT */}
              {valoarePlatitaAntecontract && parseFloat(valoarePlatitaAntecontract) > 0 && (
                <>
                  <hr className="border-purple-300" />
                  
                  <div className="p-4 bg-purple-50 rounded-lg border-2 border-purple-300">
                    <h3 className="text-xl font-bold text-purple-700 flex items-center gap-2 mb-4">
                      <Receipt className="w-6 h-6" />
                      TAXE ANTECONTRACT
                    </h3>
                    
                    {(() => {
                      const valPlatita = moneda === 'EUR' 
                        ? parseFloat(valoarePlatitaAntecontract) * CURS_EUR 
                        : parseFloat(valoarePlatitaAntecontract);
                      const onorariuAntecontract = calculeazaOnoraruAntecontract(valPlatita);
                      const tvaOnorariu = Math.round(onorariuAntecontract * (tvaPercent / 100));
                      const onorariuCuTVA = onorariuAntecontract + tvaOnorariu;
                      const extrasCFInformare = includeExtrasCFInformare ? ANTECONTRACT.EXTRAS_CF_INFORMARE * numarCF : 0;
                      const extrasCFAutentificare = includeExtrasCFAutentificare ? ANTECONTRACT.EXTRAS_CF_AUTENTIFICARE * numarCF : 0;
                      const notareCF = includeNotareCF ? ANTECONTRACT.NOTARE_CF : 0;
                      const totalAntecontract = onorariuCuTVA + extrasCFInformare + extrasCFAutentificare + notareCF;
                      
                      return (
                        <div className="space-y-3">
                          <div className="flex justify-between items-center py-2 border-b border-purple-200">
                            <div>
                              <span>Onorariu notarial antecontract</span>
                              <span className="text-xs text-purple-600 ml-1">(1% din {formatCurrency(valPlatita)})</span>
                            </div>
                            <span className="font-medium">{formatCurrency(onorariuAntecontract)}</span>
                          </div>
                          <div className="flex justify-between items-center py-1 text-blue-600">
                            <span>TVA aplicat de {tvaPercent}%</span>
                            <span>{formatCurrency(tvaOnorariu)}</span>
                          </div>
                          <div className="flex justify-between items-center py-2 bg-purple-100 px-2 rounded font-semibold">
                            <span>Onorariu cu TVA inclus</span>
                            <span>{formatCurrency(onorariuCuTVA)}</span>
                          </div>
                          
                          {includeExtrasCFInformare && (
                            <div className="flex justify-between items-center py-2 border-b border-purple-200">
                              <span>Extras CF de informare ({numarCF} × {ANTECONTRACT.EXTRAS_CF_INFORMARE} lei)</span>
                              <span className="font-medium">{formatCurrency(extrasCFInformare)}</span>
                            </div>
                          )}
                          
                          {includeExtrasCFAutentificare && (
                            <div className="flex justify-between items-center py-2 border-b border-purple-200">
                              <span>Extras CF de autentificare ({numarCF} × {ANTECONTRACT.EXTRAS_CF_AUTENTIFICARE} lei)</span>
                              <span className="font-medium">{formatCurrency(extrasCFAutentificare)}</span>
                            </div>
                          )}
                          
                          {includeNotareCF && (
                            <div className="flex justify-between items-center py-2 border-b border-purple-200">
                              <span>Notare promisiune bilaterală în CF</span>
                              <span className="font-medium">{formatCurrency(notareCF)}</span>
                            </div>
                          )}
                          
                          <div className="flex justify-between items-center py-3 bg-purple-200 px-3 rounded-lg font-bold text-lg mt-2">
                            <span>TOTAL TAXE ANTECONTRACT</span>
                            <span className="text-purple-800">{formatCurrency(totalAntecontract)}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Notă informativă */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <p className="font-semibold mb-1">⚠️ Important:</p>
                      <p>
                        Extrasul CF de informare se eliberează de obicei înainte de semnare pentru verificarea situației juridice.
                        Notarea în CF protejează drepturile cumpărătorului împotriva vânzării ulterioare către terți.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Formular pentru Vânzare-Cumpărare */}
        {tipTranzactie === 'vanzare' && (
        <Card className="mt-4 border-2 border-red-200">
          <CardContent className="p-6 space-y-6">
            {/* Valoare */}
            <div className="space-y-2">
              <Label className="text-base font-medium">
                Preț vânzare / Valoare imobil ({moneda})*
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={valoareDeclarata}
                  onChange={(e) => setValoareDeclarata(e.target.value)}
                  placeholder="0"
                  className="text-lg font-semibold"
                />
                <Button
                  variant={moneda === 'EUR' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setMoneda('EUR')}
                  className={moneda === 'EUR' ? 'bg-blue-600' : ''}
                >
                  €
                </Button>
                <Button
                  variant={moneda === 'RON' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setMoneda('RON')}
                  className={moneda === 'RON' ? 'bg-blue-600' : ''}
                >
                  Lei
                </Button>
              </div>
            </div>

            {/* Valoare studiu piață (opțional) */}
            <div className="space-y-2">
              <Label className="text-base font-medium flex items-center gap-2">
                Valoare din studiul de piață
                <span className="text-xs text-gray-400">(opțional)</span>
              </Label>
              <Input
                type="number"
                value={valoareStudiu}
                onChange={(e) => setValoareStudiu(e.target.value)}
                placeholder="Dacă e mai mare decât prețul declarat"
              />
              <p className="text-xs text-gray-500">
                Se folosește valoarea mai mare pentru calcul (conform Cod Fiscal)
              </p>
            </div>

            {/* Mod plată */}
            <div className="space-y-2">
              <Label className="text-base font-medium">Mod plată</Label>
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="modPlata"
                    checked={modPlata === 'fara_credit'}
                    onChange={() => {
                      setModPlata('fara_credit');
                      setEsteRefinantare(false);
                    }}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span>Fără credit</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="modPlata"
                    checked={modPlata === 'credit_ipotecar'}
                    onChange={() => setModPlata('credit_ipotecar')}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span>Credit ipotecar</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="modPlata"
                    checked={modPlata === 'credit_noua_casa'}
                    onChange={() => {
                      setModPlata('credit_noua_casa');
                      setProgramSpecial('PRIMA_CASA');
                    }}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span>Credit Noua Casă</span>
                </label>
              </div>
            </div>

            {/* Câmpuri pentru Credit Ipotecar / Refinanțare */}
            {(modPlata === 'credit_ipotecar' || modPlata === 'credit_noua_casa') && (
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2">
                  <Landmark className="w-5 h-5 text-blue-600" />
                  <Label className="text-base font-semibold text-blue-800">Taxe Contract Ipotecă</Label>
                </div>
                
                {/* Valoarea creditului */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Valoarea creditului ipotecar ({moneda})
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={valoareCredit}
                      onChange={(e) => setValoareCredit(e.target.value)}
                      placeholder="Ex: 300000"
                      className="flex-1"
                    />
                    <span className="text-sm text-gray-600">{moneda === 'EUR' ? '€' : 'Lei'}</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Introdu valoarea creditului pentru calculul taxelor de ipotecă
                  </p>
                </div>

                {/* Checkbox Refinanțare */}
                <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <input 
                    type="checkbox" 
                    id="refinantare"
                    checked={esteRefinantare}
                    onChange={(e) => setEsteRefinantare(e.target.checked)}
                    className="w-5 h-5 text-amber-600 mt-0.5"
                  />
                  <div>
                    <Label htmlFor="refinantare" className="font-medium text-amber-800 cursor-pointer">
                      Este refinanțare (radiere ipotecă veche)
                    </Label>
                    <p className="text-xs text-amber-700 mt-1">
                      <strong>Radierea ipotecii</strong> = ștergerea din Cartea Funciară a ipotecii vechi când creditul a fost rambursat sau refinanțat. 
                      Se plătește onorariu notar (~150 lei + TVA) + taxă ANCPI (60 lei).
                    </p>
                  </div>
                </div>

                {/* Info tranșe ipotecă */}
                <div className="text-xs text-blue-700 bg-blue-100 p-2 rounded">
                  <p className="font-semibold mb-1">📋 Tranșe onorariu contract ipotecă:</p>
                  <ul className="space-y-0.5">
                    <li>• până la 50.000 lei: 0,85% (minim 150 lei)</li>
                    <li>• 50.001 - 100.000 lei: 425 + 0,5% peste 50.000</li>
                    <li>• 100.001 - 200.000 lei: 750 + 0,46% peste 100.000</li>
                    <li>• 200.001 - 500.000 lei: 1.209 + 0,19% peste 200.000</li>
                    <li>• peste 500.000 lei: 1.778 + 0,10% peste 500.000</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Durată deținere */}
            <div className="space-y-2">
              <Label className="text-base font-medium">
                Imobil deținut
              </Label>
              <div className="flex gap-3">
                <Button
                  variant={durataDetinere === 'sub_3_ani' ? 'default' : 'outline'}
                  onClick={() => setDurataDetinere('sub_3_ani')}
                  className={durataDetinere === 'sub_3_ani' ? 'bg-red-400 hover:bg-red-500' : ''}
                >
                  sub 3 ani
                </Button>
                <Button
                  variant={durataDetinere === 'peste_3_ani' ? 'default' : 'outline'}
                  onClick={() => setDurataDetinere('peste_3_ani')}
                  className={durataDetinere === 'peste_3_ani' ? 'bg-red-400 hover:bg-red-500' : ''}
                >
                  peste 3 ani
                </Button>
              </div>
            </div>

            {/* Program special */}
            <div className="space-y-2">
              <Label className="text-base font-medium">Program special</Label>
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="programSpecial"
                    checked={programSpecial === 'niciunul'}
                    onChange={() => setProgramSpecial('niciunul')}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span>Niciunul</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="programSpecial"
                    checked={programSpecial === 'PRIMA_CASA'}
                    onChange={() => setProgramSpecial('PRIMA_CASA')}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span>Prima Casă / Noua Casă (-30%)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="programSpecial"
                    checked={programSpecial === 'DARE_IN_PLATA'}
                    onChange={() => setProgramSpecial('DARE_IN_PLATA')}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span>Dare în plată (-50%)</span>
                </label>
              </div>
            </div>

            {/* =============================================
                REZULTATE - STRUCTURĂ NOUĂ 2026
                ============================================= */}
            {rezultate && (
              <>
                {/* ========== I. TAXE CUMPĂRĂTOR ========== */}
                <div className="mt-6 p-4 bg-green-50 rounded-lg border-2 border-green-300">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-green-700 flex items-center gap-2">
                      <Receipt className="w-6 h-6" />
                      I. TAXE CUMPĂRĂTOR
                    </h3>
                    <span className="bg-green-500 text-white text-sm px-3 py-1 rounded-full font-bold">
                      TOTAL: {formatCurrency(rezultate.taxeCumparator.total)}
                    </span>
                  </div>
                  
                  {/* A. Contract Vânzare-Cumpărare */}
                  <div className="mb-4">
                    <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                      <Home className="w-4 h-4" />
                      A. Contract Vânzare-Cumpărare
                    </h4>
                    <div className="space-y-2 pl-4">
                      <div className="flex justify-between items-center py-1 border-b border-green-200">
                        <div>
                          <span>Onorariu notarial vânzare</span>
                          <span className="text-xs text-green-600 ml-1">(fără TVA)</span>
                        </div>
                        <span className="font-medium">{formatCurrency(rezultate.onorariuVanzareDupaReducere)}</span>
                      </div>
                      {rezultate.reducereProcent > 0 && (
                        <div className="flex justify-between items-center py-1 text-blue-600 text-sm">
                          <span>↳ Reducere aplicată ({rezultate.reducereAplicata})</span>
                          <span>-{rezultate.reducereProcent}%</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center py-1 text-blue-600">
                        <span>TVA aplicat de {tvaPercent}%</span>
                        <span>{formatCurrency(rezultate.tvaOnorariuVanzare)}</span>
                      </div>
                      <div className="flex justify-between items-center py-1 bg-green-100 px-2 rounded font-semibold">
                        <span>Onorariu cu TVA inclus</span>
                        <span>{formatCurrency(rezultate.onorariuVanzareCuTVA)}</span>
                      </div>
                      <div className="flex justify-between items-center py-1 border-b border-green-200">
                        <div>
                          <span>Taxa ANCPI - intabulare proprietate</span>
                          <span className="text-xs text-gray-500 ml-1">(0,15%)</span>
                        </div>
                        <span className="font-medium">{formatCurrency(rezultate.taxaANCPI_Proprietate)}</span>
                      </div>
                      <div className="flex justify-between items-center py-1 border-b border-green-200">
                        <span>Extras Carte Funciară de Autentificare / carte funciară</span>
                        <span className="font-medium">{formatCurrency(rezultate.extrasCF)}</span>
                      </div>
                    </div>
                  </div>

                  {/* B. Contract Ipotecă (dacă există) */}
                  {rezultate.taxeIpoteca?.areIpoteca && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                        <Landmark className="w-4 h-4" />
                        B. Contract Ipotecă
                        <span className="text-xs bg-blue-200 px-2 py-0.5 rounded">
                          Credit: {formatCurrency(rezultate.taxeIpoteca.valoareCredit)}
                        </span>
                      </h4>
                      <div className="space-y-2 pl-4">
                        <div className="flex justify-between items-center py-1 border-b border-blue-200">
                          <div>
                            <span>Onorariu notarial ipotecă</span>
                            <span className="text-xs text-blue-600 ml-1">(fără TVA)</span>
                          </div>
                          <span className="font-medium">{formatCurrency(rezultate.taxeIpoteca.onorariuIpoteca)}</span>
                        </div>
                        <div className="flex justify-between items-center py-1 text-blue-600">
                          <span>TVA aplicat de {tvaPercent}%</span>
                          <span>{formatCurrency(rezultate.taxeIpoteca.tvaOnorariuIpoteca)}</span>
                        </div>
                        <div className="flex justify-between items-center py-1 bg-blue-100 px-2 rounded font-semibold">
                          <span>Onorariu ipotecă cu TVA inclus</span>
                          <span>{formatCurrency(rezultate.taxeIpoteca.onorariuIpotecaCuTVA)}</span>
                        </div>
                        <div className="flex justify-between items-center py-1 border-b border-blue-200">
                          <div>
                            <span>Taxa ANCPI - intabulare ipotecă</span>
                            <span className="text-xs text-gray-500 ml-1">(0,1% + 100 lei)</span>
                          </div>
                          <span className="font-medium">{formatCurrency(rezultate.taxeIpoteca.taxaANCPI_Ipoteca)}</span>
                        </div>
                        <div className="flex justify-between items-center py-1 border-b border-blue-200">
                          <div>
                            <span>Verificare procură bancă (INFONOT)</span>
                            <span className="text-xs text-blue-600 ml-1">(+TVA)</span>
                          </div>
                          <span className="font-medium">{formatCurrency(rezultate.taxeIpoteca.verificareProcuraCuTVA)}</span>
                        </div>
                        <div className="flex justify-between items-center py-1 border-b border-blue-200">
                          <span>Verificare regim matrimonial (RNNRM)</span>
                          <span className="font-medium">{formatCurrency(rezultate.taxeIpoteca.verificareRegimMatrimonial)}</span>
                        </div>
                        
                        {/* Radiere ipotecă veche - pentru refinanțare */}
                        {rezultate.taxeIpoteca.areRadiere && (
                          <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                            <p className="text-xs font-semibold text-amber-700 mb-1">📋 Radiere ipotecă veche (refinanțare):</p>
                            <div className="flex justify-between items-center py-1 text-sm">
                              <span>Onorariu radiere (+TVA)</span>
                              <span className="font-medium text-amber-800">{formatCurrency(rezultate.taxeIpoteca.onorariuRadiereCuTVA)}</span>
                            </div>
                            <div className="flex justify-between items-center py-1 text-sm">
                              <span>Taxă radiere CF (ANCPI)</span>
                              <span className="font-medium text-amber-800">{formatCurrency(rezultate.taxeIpoteca.taxaRadiereCF)}</span>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex justify-between items-center py-2 bg-blue-200 px-2 rounded font-bold mt-2">
                          <span>Subtotal taxe ipotecă:</span>
                          <span className="text-blue-800">{formatCurrency(rezultate.taxeIpoteca.totalIpoteca)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* ========== II. TAXE VÂNZĂTOR ========== */}
                <div className="mt-4 p-4 bg-red-50 rounded-lg border-2 border-red-300">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-red-700 flex items-center gap-2">
                      <Receipt className="w-6 h-6" />
                      II. TAXE VÂNZĂTOR
                    </h3>
                    <span className="bg-red-500 text-white text-sm px-3 py-1 rounded-full font-bold">
                      TOTAL: {formatCurrency(rezultate.taxeVanzator.total)}
                    </span>
                  </div>
                  
                  <div className="space-y-2 pl-4">
                    <div className="flex justify-between items-center py-2 border-b border-red-200">
                      <div>
                        <span className="font-medium">Impozit pe transferul proprietății</span>
                        <span className="text-xs text-gray-500 ml-2">
                          ({rezultate.procentImpozit}% - deținut {durataDetinere === 'sub_3_ani' ? '≤ 3 ani' : '> 3 ani'})
                        </span>
                      </div>
                      <span className="font-bold text-lg text-red-700">{formatCurrency(rezultate.impozitTransfer)}</span>
                    </div>
                    <p className="text-xs text-gray-500 italic">
                      * Impozitul este încasat de notar și virat la stat. FĂRĂ TVA.
                    </p>
                  </div>
                </div>

                {/* Toggle detalii */}
                <Button 
                  variant="ghost" 
                  onClick={() => setShowDetails(!showDetails)}
                  className="w-full text-blue-600 mt-4"
                >
                  {showDetails ? 'Ascunde detalii calcul' : 'Vezi detalii calcul'}
                  {showDetails ? <ChevronUp className="ml-2 w-4 h-4" /> : <ChevronDown className="ml-2 w-4 h-4" />}
                </Button>

                {showDetails && (
                  <div className="bg-gray-50 p-4 rounded-lg space-y-3 text-sm mt-2">
                    <p className="font-semibold text-gray-700 border-b pb-2">📊 Detalii calcul:</p>
                    <div className="flex justify-between">
                      <span>Valoare de calcul:</span>
                      <span className="font-semibold">{formatCurrency(rezultate.valoareCalcul)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Onorariu notarial vânzare (brut):</span>
                      <span>{formatCurrency(rezultate.onorariuVanzare)}</span>
                    </div>
                    {rezultate.reducereProcent > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Reducere aplicată ({rezultate.reducereAplicata}):</span>
                        <span>-{rezultate.reducereProcent}%</span>
                      </div>
                    )}
                    
                    <hr className="border-gray-300" />
                    
                    <p className="font-semibold text-gray-600">Cu TVA vs Fără TVA:</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="p-2 bg-blue-50 rounded">
                        <p className="font-semibold text-blue-700">✔ CU TVA {tvaPercent}%:</p>
                        <ul className="mt-1 space-y-0.5">
                          <li>• Onorariu notarial vânzare</li>
                          <li>• Onorariu notarial ipotecă</li>
                          <li>• Verificare procură INFONOT</li>
                        </ul>
                      </div>
                      <div className="p-2 bg-gray-100 rounded">
                        <p className="font-semibold text-gray-700">❌ FĂRĂ TVA:</p>
                        <ul className="mt-1 space-y-0.5">
                          <li>• Taxe ANCPI (intabulare)</li>
                          <li>• Extras CF</li>
                          <li>• Verificare regim matrimonial</li>
                          <li>• Impozit pe transfer</li>
                        </ul>
                      </div>
                    </div>
                    
                    {rezultate.taxeIpoteca?.areIpoteca && (
                      <>
                        <hr className="border-blue-300" />
                        <div className="bg-blue-50 p-2 rounded space-y-2">
                          <p className="font-semibold text-blue-800">📋 Detalii taxe ipotecă:</p>
                          <div className="flex justify-between">
                            <span>Valoare credit:</span>
                            <span>{formatCurrency(rezultate.taxeIpoteca.valoareCredit)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Onorariu ipotecă (fără TVA):</span>
                            <span>{formatCurrency(rezultate.taxeIpoteca.onorariuIpoteca)}</span>
                          </div>
                          <div className="flex justify-between text-blue-600">
                            <span>TVA ipotecă ({tvaPercent}%):</span>
                            <span>+{formatCurrency(rezultate.taxeIpoteca.tvaOnorariuIpoteca)}</span>
                          </div>
                          <div className="flex justify-between font-semibold">
                            <span>Total onorariu ipotecă cu TVA:</span>
                            <span>{formatCurrency(rezultate.taxeIpoteca.onorariuIpotecaCuTVA)}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
        )}
      </Tabs>

      {/* Notă obligatorie */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <p className="font-semibold mb-1">⚠️ Notă importantă:</p>
          <p>
            Valorile sunt orientative, calculate conform legislației în vigoare (Ordin 177/C/2024, Cod Fiscal). 
            TVA-ul de {tvaPercent}% este aplicat la onorariul notarial conform legislației în vigoare.
            Notarul poate ajusta onorariul în funcție de complexitatea actului. 
            Impozitul este încasat de notar și virat la stat.
          </p>
        </div>
      </div>

      {/* Info legislație */}
      <div className="text-xs text-gray-400 text-center">
        Calculator actualizat conform: Ordin 177/C/2024 (onorarii notariale), Cod Fiscal (impozite), 
        Tarife ANCPI (carte funciară), TVA {tvaPercent}%
      </div>
    </div>
  );
};

export default NotaryFeesCalculator;
